import { CARD_COLORS } from "@mn/shared";
import { useEffect } from "react";
import { useT } from "@/lib/i18n";
import { Actions } from "./actions";
import { CARD_COLOR_CSS } from "./colors";
import { useBoardUI } from "./store";

export function ContextMenu({ actions, canEdit }: { actions: Actions; canEdit: boolean }) {
  const menu = useBoardUI((s) => s.contextMenu);
  const close = () => useBoardUI.getState().setContextMenu(null);
  const t = useT();
  useEffect(() => {
    if (!menu) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    const onDown = (e: PointerEvent) => {
      if ((e.target as HTMLElement).closest?.("[data-context-menu]")) return;
      close();
    };
    window.addEventListener("pointerdown", onDown, { capture: true });
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onDown, { capture: true });
    };
  }, [menu]);
  if (!menu) return null;
  // Keep the menu inside the viewport: flip left/up near the right and bottom edges.
  const vp = useBoardUI.getState().viewport;
  const MENU_W = 240;
  const MENU_H = menu.cardId ? 420 : 300;
  const left = menu.sx + MENU_W > vp.w ? Math.max(0, menu.sx - MENU_W) : menu.sx;
  const top = menu.sy + MENU_H > vp.h ? Math.max(0, Math.min(menu.sy, vp.h - MENU_H)) : menu.sy;
  const item = (label: string | undefined, fn: () => void, danger = false) => (
    <button
      type="button"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={() => (fn(), close())}
      className={`block w-full px-3 py-1.5 text-left text-sm hover:bg-panel-2 ${danger ? "text-error" : ""}`}
    >
      {label}
    </button>
  );
  return (
    <div
      data-context-menu
      className="absolute z-40 min-w-48 rounded-md border border-border bg-panel py-1 shadow-panel"
      style={{ left, top, maxHeight: vp.h - 8, overflowY: "auto" }}
      role="menu"
    >
      {menu.cardId ? (
        <>
          {canEdit ? (
            <>
              <div className="flex flex-wrap gap-1 px-3 py-2" onPointerDown={(e) => e.stopPropagation()}>
                {CARD_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    title={c}
                    onClick={() => (actions.setColor(c), close())}
                    className="size-5 rounded-full border border-border"
                    style={{
                      background: CARD_COLOR_CSS[c]?.bg,
                      backgroundImage:
                        c === "transparent"
                          ? "linear-gradient(45deg,#444 25%,transparent 25%,transparent 75%,#444 75%)"
                          : undefined,
                    }}
                  />
                ))}
              </div>
              {item(t.menu.labels, () =>
                useBoardUI.getState().setPicker({ kind: "labels", sx: menu.sx, sy: menu.sy }),
              )}
              {item(t.menu.reaction, () =>
                useBoardUI.getState().setPicker({ kind: "reactions", sx: menu.sx, sy: menu.sy }),
              )}
              {menu.cardId &&
              ["board", "alias"].includes(actions.cards.get(menu.cardId)?.get("type") as string)
                ? item(t.menu.openBoard, () => actions.openTile(menu.cardId!))
                : null}
              {menu.cardId && actions.cards.get(menu.cardId)?.get("type") === "board"
                ? item(t.menu.duplicateBoard, () =>
                    window.dispatchEvent(
                      new CustomEvent("mn:duplicate-board", {
                        detail: { boardId: actions.cards.get(menu.cardId!)?.get("childBoardId") },
                      }),
                    ),
                  )
                : null}
              {menu.cardId && actions.cards.get(menu.cardId)?.get("type") === "board"
                ? item(t.menu.boardSettings, () =>
                    window.dispatchEvent(
                      new CustomEvent("mn:board-settings", {
                        detail: { boardId: actions.cards.get(menu.cardId!)?.get("childBoardId") },
                      }),
                    ),
                  )
                : null}
              {menu.cardId && actions.cards.get(menu.cardId)?.get("type") === "board"
                ? item(t.convertToTemplate, () =>
                    window.dispatchEvent(
                      new CustomEvent("mn:convert-template", {
                        detail: { boardId: actions.cards.get(menu.cardId!)?.get("childBoardId") },
                      }),
                    ),
                  )
                : null}
              {menu.cardId &&
              ["note", "clone"].includes(actions.cards.get(menu.cardId)?.get("type") as string)
                ? item(t.menu.makeSynced, () => actions.cloneSynced(menu.cardId!))
                : null}
              {menu.cardId &&
              ["note", "clone"].includes(actions.cards.get(menu.cardId)?.get("type") as string)
                ? item(t.menu.copySynced, () => actions.copySynced(menu.cardId!))
                : null}
              {actions.selectedIds().length >= 2 ? (
                <div className="px-3 py-1.5" onPointerDown={(e) => e.stopPropagation()}>
                  <div className="mb-1 text-[11px] uppercase tracking-wide text-dim">{t.menu.align}</div>
                  <div className="flex flex-wrap gap-1">
                    {(
                      [
                        ["left", "⇤", t.menu.alignLeft],
                        ["centerX", "⇔", t.menu.alignCenterX],
                        ["right", "⇥", t.menu.alignRight],
                        ["top", "⤒", t.menu.alignTop],
                        ["centerY", "⇕", t.menu.alignCenterY],
                        ["bottom", "⤓", t.menu.alignBottom],
                      ] as const
                    ).map(([how, glyph, label]) => (
                      <button
                        key={how}
                        type="button"
                        title={label}
                        onClick={() => (actions.align(how), close())}
                        className="size-7 rounded-sm border border-border text-sm hover:bg-panel-2"
                      >
                        {glyph}
                      </button>
                    ))}
                    {actions.selectedIds().length >= 3 ? (
                      <>
                        <button
                          type="button"
                          title={t.menu.distributeX}
                          onClick={() => (actions.distribute("x"), close())}
                          className="h-7 rounded-sm border border-border px-1.5 text-sm hover:bg-panel-2"
                        >
                          ↔
                        </button>
                        <button
                          type="button"
                          title={t.menu.distributeY}
                          onClick={() => (actions.distribute("y"), close())}
                          className="h-7 rounded-sm border border-border px-1.5 text-sm hover:bg-panel-2"
                        >
                          ↕
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>
              ) : null}
              {item(t.menu.toUnsorted, () => actions.toUnsorted())}
              {item(t.menu.duplicate, () => actions.duplicate())}
              {item(t.menu.bringToFront, () => actions.front())}
              {item(t.menu.sendToBack, () => actions.back())}
              {item(t.menu.lock, () => actions.toggleLock())}
              <div className="my-1 border-t border-border" />
              {item(t.moveToTrash, () => actions.delete(), true)}
            </>
          ) : null}
        </>
      ) : (
        <>
          {canEdit ? item(t.menu.newNote, () => void actions.create("note", menu.px, menu.py)) : null}
          {canEdit ? item(t.menu.newHeading, () => void actions.create("heading", menu.px, menu.py)) : null}
          {canEdit ? item(t.menu.newBoard, () => void actions.create("board", menu.px, menu.py)) : null}
          {canEdit
            ? item(t.menu.boardShortcut, () =>
                useBoardUI
                  .getState()
                  .setPicker({ kind: "alias", sx: menu.sx, sy: menu.sy, px: menu.px, py: menu.py }),
              )
            : null}
          {canEdit ? item(t.menu.draw, () => void actions.create("annotation", menu.px, menu.py)) : null}
          {canEdit && Actions.syncedClip()
            ? item(t.menu.pasteSynced, () => void actions.pasteSynced(menu.px, menu.py))
            : null}
          {canEdit
            ? item(`${actions.preventOverlap ? "✓ " : ""}${t.menu.preventOverlap}`, () =>
                actions.setPreventOverlap(!actions.preventOverlap),
              )
            : null}
          {canEdit ? item(t.menu.paste, () => document.execCommand("paste")) : null}
          {item(t.menu.zoomFit, () => window.dispatchEvent(new CustomEvent("mn:zoom-fit")))}
          {canEdit
            ? item(t.menu.boardSettings, () =>
                window.dispatchEvent(new CustomEvent("mn:board-settings", { detail: {} })),
              )
            : null}
        </>
      )}
    </div>
  );
}
